// Use Box root folder unless you have a verified numeric ID

// Testing
// const RAW_ROOT_FOLDER_ID = "180297636323"; 

// Production
const RAW_ROOT_FOLDER_ID = "176509898817"; 

// Normalize Box folder ID (remove "d_" or "f_" prefixes) and return as number
const normalizeId = (id: string | number): number => {
  const cleaned = id.toString().replace(/^d_/, "").replace(/^f_/, "");
  const num = Number(cleaned);
  if (isNaN(num)) throw new Error(`Invalid Box folder ID: ${id}`);
  return num;
};

// This is the actual root ID used everywhere
const ROOT_FOLDER_ID = normalizeId(RAW_ROOT_FOLDER_ID);

// Basic sanitization (Box naming rules)
const sanitizeName = (name: string) => {
  let sanitized = name.trim();
  sanitized = sanitized.replace(/[\\\/\x00-\x1F\x7F-\uFFFF]/g, "_");
  if (sanitized === "." || sanitized === ".." || sanitized.length === 0)
    sanitized = "_";
  return sanitized;
};

// Always returns safe array of folder items
const listFolderItems = async (folderId: number, accessToken: string) => {
  try {
    const res = await fetch(
      `https://api.box.com/2.0/folders/${folderId}/items?limit=1000&fields=id,name,type`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!res.ok) {
      console.warn("Box Folder List Failed:", folderId, res.status);
      return [];
    }

    const json = await res.json();
    return Array.isArray(json.entries) ? json.entries : [];
  } catch (err) {
    console.error("Box listFolderItems error:", err);
    return [];
  }
};

// Create folder safely; prevents `.find` on undefined
export const getOrCreateFolder = async (
  parentId: number | string,
  folderName: string,
  accessToken: string
): Promise<number> => {
  const safeName = sanitizeName(folderName);
  parentId = normalizeId(parentId);

  const entries = await listFolderItems(parentId, accessToken);
  const match = entries.find(
    (e: any) => e.type === "folder" && e.name === safeName
  );

  if (match) return Number(match.id);

  const createRes = await fetch("https://api.box.com/2.0/folders", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name: safeName, parent: { id: parentId } }),
  });

  if (!createRes.ok) {
    const err = await createRes.text();
    console.error("Box Folder Creation Failed:", createRes.status, err);

    throw new Error(
      `Unable to create Box folder. Parent ID = ${parentId}, Name = ${safeName}`
    );
  }

  const json = await createRes.json();
  return Number(json.id);
};

// Ensure uploaded filename is unique
const getUniqueFileName = (original: string, existingNames: string[]) => {
  const extIndex = original.lastIndexOf(".");
  const base = extIndex > -1 ? original.slice(0, extIndex) : original;
  const ext = extIndex > -1 ? original.slice(extIndex) : "";

  let candidate = original;
  let i = 1;

  while (existingNames.includes(candidate)) {
    candidate = `${base}(${i})${ext}`;
    i++;
  }

  return candidate;
};

// Uploads one file safely
export const uploadInvoiceFile = async (
  file: File,
  folderId: number,
  accessToken: string
): Promise<{ success: boolean; fileId?: string }> => {
  const entries = await listFolderItems(folderId, accessToken);
  const existingNames = entries
    .filter((e: any) => e.type === "file")
    .map((e: any) => e.name);

  const uniqueName = sanitizeName(getUniqueFileName(file.name, existingNames));

  return new Promise((resolve) => {
    const formData = new FormData();

    formData.append(
      "attributes",
      JSON.stringify({ name: uniqueName, parent: { id: folderId } })
    );

    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", "https://upload.box.com/api/2.0/files/content");
    xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const resp = JSON.parse(xhr.responseText);
        resolve({ success: true, fileId: resp.entries?.[0]?.id });
      } else {
        console.error("Box upload error:", xhr.status, xhr.responseText);
        resolve({ success: false });
      }
    };

    xhr.onerror = () => {
      console.error("XHR Upload Error");
      resolve({ success: false });
    };

    xhr.send(formData);
  });
};

// MAIN FUNCTION — Upload multiple invoice files
export const uploadInvoiceFiles = async (
  labName: string,
  accessToken: string,
  files: File[]
) => {
  if (!labName) throw new Error("Missing lab name");
  if (!accessToken) throw new Error("Missing access token");

  const rootFolderId = ROOT_FOLDER_ID; // already numeric

  // "Invoices" parent folder under root
  const invoicesFolderId = await getOrCreateFolder(
    rootFolderId,
    "Invoices",
    accessToken
  );

  // Lab folder inside "Invoices"
  const labFolderId = await getOrCreateFolder(
    invoicesFolderId,
    labName,
    accessToken
  );

  // Upload all files
  const results = await Promise.all(
    files.map((file) => uploadInvoiceFile(file, labFolderId, accessToken))
  );

  const uploaded = results
    .filter((r) => r.success && r.fileId)
    .map((r) => r.fileId as string);

  return {
    labName,
    uploadedFileIds: uploaded,
    folderId: labFolderId,
  };
};
